import { useEffect, useRef } from "react";

/**
 * Open a section's ADD form once on arrival — Create Rx's "+ Add complaint /
 * diagnosis / lab investigation / medicine" lands on the patient screen with
 * `?section=<key>&add=1`, and the doctor asked for the add form, not just the
 * section (product, 2026-09-16).
 *
 * The host reads and STRIPS the flag (so back/forward, a reload or a remount
 * never re-opens it) and hands the active section `autoOpenAdd`; the section
 * calls this with the same `open` its own add button calls.
 *
 * AFTER THE LIST HAS LOADED, not on mount, for two reasons:
 *   · EventService is a bare EventEmitter with no replay, and in the mount
 *     commit a section's effects run before its sibling Editor's listener and
 *     before PatientShell's RAIL_OPEN listener — a mount-time emit is lost, or
 *     pushes a sheet into a column still marked hidden;
 *   · the add controls (and Lab's modal, which needs the doc's version) only
 *     exist once the list is on screen.
 * On a load error the flag is consumed and nothing opens — there is no add
 * button in that state either.
 *
 * Fires at most once per mount (the ref), and `onAutoOpenAdd` clears the host's
 * flag so a later visit to the tab in the same page does not re-open it.
 */
const useAutoOpenAdd = ({ autoOpenAdd, onAutoOpenAdd, loading, error, doc, open }) => {
  const fired = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!autoOpenAdd || fired.current || loading) return;
    fired.current = true;
    if (onAutoOpenAdd) onAutoOpenAdd();
    if (doc && !error) openRef.current();
  }, [autoOpenAdd, onAutoOpenAdd, loading, error, doc]);
};

export default useAutoOpenAdd;
